import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector } from '@angular/core';
import { LL, BOOT_LOCALE } from 'i18n/i18n.service';
import { formatWeekdayDatePaddedWithYear } from '@cf-shared/date-format';
import { CsPage, NavController } from 'providers/nav-controller';
import { AuthService } from 'services/auth.service';
import { DebugService } from 'services/debug.service';
import { SettingsService } from 'services/settings.service';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { ConfigDbService } from 'services/config-db.service';
import { ConfigKey } from 'services/config-keys';
import { UserDb, logEntrySavedEvent, TaskStatusCacheUpdatedEvent } from 'services/user-db.service';
import { taskStatusChangeAffectsVisibleDays, TaskStatusChangeRange } from 'services/user-db.task-status';
import { DocWatcher } from 'services/database-wrapper';
import { DateTime } from 'luxon';
import { TaskStatus } from 'components/cs-task-calendar-day/cs-task-calendar-day';
import { LoadingController } from 'providers/loading-controller';
import { upperFirst } from 'lodash-es';
import { PopupMenuButton, PopupMenuProvider } from 'providers/popup-menu/popup-menu';
import { LoggingService } from 'services/logging.service';
import { IProgramSettingsPage, ProgramSettingsTab } from 'pages/program-settings-page/program-settings-interface';
import { getTransitionSlideInOutMs, waitForAngularUpdate } from 'services/browser-utils';
import { DayAnimationQueue } from 'services/day-animation-queue';
import { SingleFlightReload } from 'util/single-flight-reload';
import { ITaskCalendarPage } from 'pages/task-calendar-page/task-calendar-interface';
import { IEditWorkoutsPage } from 'pages/edit-workouts/edit-workouts-interface';
import { ITrainingOverviewPage } from 'pages/training-overview/training-overview-interface';
import { isCoachingDashboardOnAsync } from 'services/coaching-dashboard-flag';
import { IEquipmentSearchPage } from 'pages/equipment/equipment-search-interface';
import { IWorkoutDetailPage } from 'pages/workout-detail/workout-detail-interface';
import { IExerciseStatsPage } from 'pages/exercise-stats/exercise-stats-interface';
import { IExerciseSwitchPage } from 'pages/exercise-switch/exercise-switch-interface';
import { UiExercise } from 'models/workout-ui-models';
import { WorkoutLoaderService, WorkoutLoadMode, WorkoutLoadingContext } from 'services/workout-loader.service';
import { ExerciseChosenBy } from 'services/exercise-switching';
import { Log_Workout, Log_WorkoutExercise } from '@cf-shared/data-model/log-workout';
import { FitnessPlan } from '@cf-shared/data-model/fitness-plan';
import { LogRecordType, LogRecordState, getEntryLocalTimeInDesiredTimeZone } from '@cf-shared/data-model/user-log';
import {
  DaySlotData,
  DaySlotId,
  DaySlotLoad,
  TrainingSlotLoadDeps,
  buildDaySlotExercisesAsync,
  createEmptyDaySlotData,
  loadDaySlotAsync,
  loadDaySlotMetaAsync,
} from './training-slot-load';
import type { SlotPendingBuild } from './training-slot-load';
import { WorkoutOptionKind, getWorkoutOptionKinds } from './workout-options-policy';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { PubSubRegistration, PubSubService, startWorkoutFromNavEvent, workoutStartableStateChangedEvent } from 'services/pubsub.service';
import { TrainingPageParams } from './training-interface';
import { TrainingSwitchCapture, TrainingSwitchTargetKind, resolveTrainingSwitchTarget } from './training-switch-target';
import { WorkoutStateService } from 'services/workout-state.service';
import { IChangeWorkoutPage } from 'pages/change-workout-page/change-workout-interface';
import { formatRelativeDay, formatWorkoutName } from 'util/formatting';

// animation duration read from CSS variable --transition-slide-in-out-ms
const getAnimationDurationMs = () => getTransitionSlideInOutMs();

@Component({
  selector: 'page-training',
  templateUrl: 'training.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TrainingPage extends CsPage<TrainingPageParams> {
  protected LL = LL;

  hasClientSmallScreen = hasClientSmallScreen;
  protected animateDayContent = false;
  protected activeDaySlot: DaySlotId = 'A';
  protected leavingSlot: DaySlotId | null = null;
  protected enteringSlot: DaySlotId | null = null;
  protected isBack: boolean = false;
  private initialLoadDone = false;
  // #4242: hold the heavy exercise list off the DOM until the page-enter slide
  // finishes. Rendering 7-8 exercise cards costs ~100 ms of main-thread work
  // (component instantiation + paint), which drops frames if it lands during
  // the slide (the mid-slide "jam"). ionViewDidEnter fires AFTER the slide
  // completes, so revealing the list there does that render while the page is
  // stationary — where the same cost is not perceptible. Until then the page
  // shows its loading shell. A fresh instance (every bottom-nav switch) starts
  // false; it flips true once and stays true, so day-swipes and re-entries
  // render immediately.
  protected contentEntered = false;
  // #4242 / #4410: the one piece of state that carries a page entry's day load
  // across the page-enter slide. ionViewWillEnter loads the LIGHT metadata during
  // the slide (so the header title + Start button show) and ionViewDidEnter builds
  // the heavy exercise list after it. Set on BOTH first entry (off the enter
  // slide) and warm re-entry from a child page (off the back/pop slide, so
  // Training's resync rebuild no longer runs mid-slide and desyncs the paired
  // slide). null means there is no entry load waiting to be finished.
  //
  // The three parts are held together because they are only ever correct
  // together:
  // - `slot`: the slot the metadata was loaded for, captured before the metadata
  //   await. The loading-shell clear in ionViewDidEnter needs it even when there
  //   is nothing to build, and this.activeDaySlot could in principle change
  //   between the two hooks.
  // - `load`: the ownership claim both halves run under. They are two halves of
  //   ONE load, so they share it — a reactive reload that starts mid-slide takes
  //   a newer claim and the deferred build then stops instead of writing a stale
  //   list over the fresher one.
  // - `build`: the workout whose exercise list still needs building, or null when
  //   there is nothing to build (no workout for the day / a skipped workout).
  private pendingBuild: { slot: DaySlotId; load: DaySlotLoad; build: SlotPendingBuild } | null = null;

  // queue for rapid day changes - prevents animation interruption
  private dayQueue = new DayAnimationQueue();

  // The two day copies, each holding that day's content AND its loading flag.
  // One object per slot, so nothing new a slot has to hold arrives as another
  // pair of lettered fields written in two hand-spelled branches. The template
  // names both slots because it draws both; the code below reaches them through
  // slotDataFor().
  protected slots: Record<DaySlotId, DaySlotData> = {
    A: createEmptyDaySlotData(),
    B: createEmptyDaySlotData(),
  };

  // Component-level plan cache (same for both slots)
  private plan: FitnessPlan;

  // getter for active slot data
  get activeSlotData(): DaySlotData {
    return this.slotDataFor(this.activeDaySlot);
  }

  // Derived properties from activeSlotData.logWorkout
  get hasWorkout(): boolean {
    return !!this.activeSlotData.logWorkout;
  }

  get workoutId(): string {
    return this.activeSlotData.logWorkout?._id || '';
  }

  get workoutName(): string {
    const logWorkout = this.activeSlotData.logWorkout;
    if (!logWorkout || !this.plan) return '';
    // the stored name is canonical English, so translate it for display
    return formatWorkoutName(this.workoutLoader.getEffectiveWorkoutName(logWorkout, this.plan)) || LL.training.workout();
  }

  get workoutPlanReference(): number {
    const logWorkout = this.activeSlotData.logWorkout;
    if (!logWorkout || !this.plan) return 0;
    return this.workoutLoader.getEffectivePlanReference(logWorkout, this.plan);
  }

  get isSkipped(): boolean {
    return this.activeSlotData.logWorkout?.state === LogRecordState.Skipped;
  }

  get isInProgress(): boolean {
    return this.activeSlotData.logWorkout?.state === LogRecordState.Incomplete;
  }

  private get isHardFuture(): boolean {
    return this.activeSlotData.workoutMode === WorkoutLoadMode.HardFuture;
  }

  // whether the current workout can be started (vs just opened/viewed)
  // true when: has workout with Current/SoftFuture mode, OR no workout but viewing today
  get canStartWorkout(): boolean {
    const mode = this.activeSlotData.workoutMode;
    const isStartableMode = mode === WorkoutLoadMode.Current || mode === WorkoutLoadMode.SoftFuture;

    // if there's a workout, check if it's startable based on mode
    if (this.hasWorkout) {
      return isStartableMode;
    }

    // if no workout, only allow starting if viewing today
    const now = this.debug.getNowLocal();
    const isToday = this.currentDay.hasSame(now, 'day');
    return isToday;
  }

  get workoutButtonText(): string {
    if (this.isInProgress) {
      return LL.training.resumeWorkout();
    }
    if (this.canStartWorkout) {
      return LL.training.startWorkout();
    }
    // if hard future, show "Edit Workout" as primary action
    if (this.isHardFuture) {
      return LL.training.editWorkoutShort();
    }
    return LL.training.openWorkout();
  }

  // whether the skipped workout can be reverted (only when viewing today)
  get canRevertSkip(): boolean {
    if (!this.isSkipped) return false;
    const now = this.debug.getNowLocal();
    return this.currentDay.hasSame(now, 'day');
  }

  // publish the startable state to app component for bottom nav
  private publishStartableState() {
    this.pubSub.publish(workoutStartableStateChangedEvent, { canStart: this.canStartWorkout });
  }

  calendarTaskStatuses: Map<string, TaskStatus> = new Map();
  currentDay: DateTime;
  currentDayLabel: string;
  fakeToday: string;
  currentWeekday: string;

  private docWatcher: DocWatcher;
  private startWorkoutSubscription: PubSubRegistration;
  private logEntrySavedReg: PubSubRegistration;
  private taskStatusCacheUpdatedReg: PubSubRegistration;


  constructor(
    public navCtrl: NavController,
    protected auth: AuthService,
    protected settings: SettingsService,
    protected debug: DebugService,
    public cloudFunctionsService: CloudFunctionsService,
    public configDb: ConfigDbService,
    private cd: ChangeDetectorRef,
    private userDb: UserDb,
    private popupMenu: PopupMenuProvider,
    private injector: Injector,
    private workoutLoader: WorkoutLoaderService,
    private logging: LoggingService,
    private pubSub: PubSubService,
    private loadingCtrl: LoadingController,
    private workoutStateService: WorkoutStateService,
  ) {
    super();
  }

  /** whether the coaching dashboard switch is on for the signed-in account; decides one item of the settings menu */
  private coachingDashboardOn = false;
  /** `ConfigKey.CompactWorkoutFeedback`: true only for an exact `true` (fails closed) */
  compactFeedback = false;
  // set once, on the first entry, so re-entering the page never subscribes a second time
  private unwatchCompactFeedback?: () => void;

  async ionViewCanEnter() {
    let isoDateLocal = this.navParams.data.day;
    if (isoDateLocal) {
      this.currentDay = DateTime.fromISO(isoDateLocal).startOf('day');
    }
    if (!this.currentDay || !this.currentDay.isValid) {
      this.currentDay = this.debug.getNowLocal().startOf('day');
    }
    // read here, so the settings menu can be built on a tap without waiting (#5456)
    this.coachingDashboardOn = await isCoachingDashboardOnAsync(this.configDb);
  }

  async ngOnInit() {
    this.watchCompactFeedbackSwitch();
    try {
      this.slots.A.loading = true;
      this.cd.markForCheck();

      let now = this.debug.getNowLocal();
      let todayBOD = now.startOf('day');
      this.currentDay = todayBOD;

      // if not faking today
      if (now.hasSame(DateTime.local(), 'day')) {
        this.fakeToday = '';
      } else {
        this.fakeToday =
          '[' +
          formatWeekdayDatePaddedWithYear(now, BOOT_LOCALE) +
          ']';
      }

      this.currentDayLabel = upperFirst(formatRelativeDay(now, this.currentDay));
      this.currentWeekday = this.currentDay.weekday.toFixed(0);

      // deliberately NOT awaited: the task-status load can block for a long
      // time behind the task-status cache mutex (worst case: a full view-index
      // rebuild over the whole log history holds it for minutes). The page
      // must enter without it — the calendar fills in when the statuses
      // arrive, instantly when the in-memory memo already has them.
      // detached from the lifecycle promise, so a rejection must be surfaced
      // here — nothing upstream catches it anymore
      void this.loadWorkoutTaskStatuses().catch(err => this.logging.trackAbnormalException(err));
    } finally {
      this.slots.A.loading = false;
      this.animateDayContent = true;
      this.safeMarkForCheck(this.cd);
    }
  }

  async loadWorkoutTaskStatuses(changedRange?: TaskStatusChangeRange) {
    const today = this.debug.getNowLocal().startOf('day');
    const endDate = today.plus({ days: 7 });
    const startDate = endDate.minus({ months: 1 });

    // skip a reload for a change that is about days this calendar does not
    // show. During a backlog sync most events are: a burst backfilling old log
    // entries publishes an old range, and reloading for it would be pure churn
    // (#4249). No range given means "unknown" — reload, the safe direction.
    //
    // The check reads the whole event, not just its bounding span. The span
    // alone still suppresses plenty — a wave lying entirely in old history
    // fails the overlap test outright. What it cannot suppress is a wave whose
    // days STRADDLE this window with none inside it, and that is what the
    // clusters are for (#4451).
    if (changedRange && !taskStatusChangeAffectsVisibleDays(
      changedRange, startDate.toISODate(), endDate.toISODate())) {
      return;
    }

    const dayStatuses = await this.userDb.getCachedTaskStatusesAsync(startDate, endDate);

    this.calendarTaskStatuses = new Map<string, TaskStatus>();
    dayStatuses.forEach((statuses, date) => {
      this.calendarTaskStatuses.set(date, statuses.workoutStatus);
    });

    this.safeMarkForCheck(this.cd);
  }

  async loadTaskStatusesForRange(startDate: DateTime, endDate: DateTime): Promise<Map<string, TaskStatus>> {
    const dayStatuses = await this.userDb.getCachedTaskStatusesAsync(startDate, endDate);

    const result = new Map<string, TaskStatus>();
    dayStatuses.forEach((statuses, date) => {
      result.set(date, statuses.workoutStatus);
    });
    return result;
  }

  async onCalendarDateSelected(date: DateTime) {
    // if animation is in progress, queue this date and return
    if (!this.dayQueue.tryStart(date)) {
      return;
    }

    // on the happy path the queue is completed by releaseDayQueueAfterSlide →
    // clearLeavingSlotState; on every other exit (throw OR viewDestroyed early-return)
    // the finally must complete it, or subsequent date clicks on a re-entered page
    // would be silently dropped (viewDestroyed flips back to false on ionViewWillEnter).
    let handedOffToAnimation = false;
    try {
      const nextSlot = this.activeDaySlot === 'A' ? 'B' : 'A';
      const leavingSlotId = this.activeDaySlot;

      // set up entering/leaving classes with correct direction
      this.isBack = date < this.currentDay;
      this.enteringSlot = nextSlot;
      this.leavingSlot = leavingSlotId;
      this.cd.markForCheck();

      // wait for Angular to apply classes
      await waitForAngularUpdate(this.injector);
      if (this.viewDestroyed) {
        // the user asked for this day and is not getting it. That is legitimate
        // when they navigated away in the meantime, but it must never be silent
        // again: this exact drop, with the answer from the task calendar page
        // arriving before the page had re-entered, made a day pick do nothing at
        // all with no trace anywhere (#4411). It is now also the detector that
        // would catch that fix coming undone.
        this.logging.trackSilentProblem('TrainingDayPickDroppedWhileLeaving', { date: date.toISODate() });
        return;
      }

      // swap active slot
      this.enteringSlot = null;
      this.activeDaySlot = nextSlot;
      this.currentDay = date;

      // set loading on next slot
      this.slotDataFor(nextSlot).loading = true;
      this.safeMarkForCheck(this.cd);

      // update URL params
      await this.navCtrl.updateCurrentPageParamsAsync({ day: date.toISODate() });
      if (this.viewDestroyed) return;

      const now = this.debug.getNowLocal();
      this.currentDayLabel = upperFirst(formatRelativeDay(now, date));
      this.currentWeekday = date.weekday.toFixed(0);
      this.safeMarkForCheck(this.cd);

      // load workout data into the next (now active) slot
      await this.loadWorkoutDataForSlot(nextSlot, date);
      if (this.viewDestroyed) return;

      // fire-and-forget: awaiting this held the day switch open and left the
      // tapped day blank — see design-docs/reactive-sync.md, "Neither day switch
      // waits for the calendar dots" (#4410)
      void this.loadWorkoutTaskStatuses().catch(err => this.logging.trackAbnormalException(err));

      // clear loading on next slot
      this.slotDataFor(nextSlot).loading = false;
      this.safeMarkForCheck(this.cd);

      // publish startable state for bottom nav (may have changed with new day)
      this.publishStartableState();

      // release the day-pick queue once the slide has had time to finish
      this.releaseDayQueueAfterSlide();
      handedOffToAnimation = true;
    } finally {
      if (!handedOffToAnimation) {
        this.dayQueue.complete();
      }
    }
  }

  // release the day-pick queue once the slide's duration has passed. The slide
  // itself, and clearing the leaving panel's classes, belong to
  // cs-tab-content-container on its own timer; this timer only decides when a
  // queued day pick may start. A fixed timer is deliberate: the page has no
  // transition of its own to listen to (df-bd8185673235).
  private releaseDayQueueAfterSlide() {
    // raw setTimeout, NOT safeTimeout: it must fire even after view destroy, or the
    // re-entered page would silently drop every date click (see clearLeavingSlotState).
    setTimeout(() => this.clearLeavingSlotState(), getAnimationDurationMs());
  }

  handleSwipeForward() {
    void this.onCalendarDateSelected(this.currentDay.plus({ days: 1 }));
  }

  handleSwipeBack() {
    void this.onCalendarDateSelected(this.currentDay.minus({ days: 1 }));
  }

  private clearLeavingSlotState() {
    // always complete the queue first; raw setTimeout in releaseDayQueueAfterSlide
    // can fire after destroy, and the queue must be cleared even then or the re-entered
    // page would silently drop every subsequent date click.
    const queued = this.dayQueue.complete();
    this.leavingSlot = null;
    this.isBack = false;
    this.safeMarkForCheck(this.cd);

    // re-dispatch a queued date only if the view is still alive — onCalendarDateSelected
    // immediately calls a raw markForCheck before any await, which would throw NG0911
    // on a torn-down view.
    if (queued && !this.viewDestroyed) {
      void this.onCalendarDateSelected(queued);
    }
  }

  // #4243: guards the reactive (watcher / logEntrySaved) reloads so a burst of
  // sync notifications collapses into at most one in-flight reload plus one
  // trailing catch-up, instead of piling up dozens of concurrent reloads. The
  // awaited navigation/entry reloads deliberately do NOT go through this guard —
  // ionViewWillEnter must run its resync to completion, not be folded away.
  private slotReload = new SingleFlightReload();

  /**
   * Reload the active slot for a reactive trigger (doc watcher or logEntrySaved),
   * serialized through the single-flight guard. Surfaces any error since the
   * caller is fire-and-forget. safeMarkForCheck self-guards on viewDestroyed, so
   * no extra post-await check is needed (NG0911).
   */
  private reactiveSlotReload(): Promise<void> {
    return this.slotReload
      .runAsync(async () => {
        if (this.viewDestroyed) return;
        await this.loadWorkoutDataForSlot(this.activeDaySlot, this.currentDay);
        this.safeMarkForCheck(this.cd);
      })
      .catch(err => this.logging.trackAbnormalException(err));
  }

  /**
   * Build the bundle of calls a slot load needs. Rebuilt per load because
   * `this.docWatcher` only exists from ionViewWillEnter onwards, and the debug
   * "fake today" setting can change between loads.
   */
  private slotLoadDeps(): TrainingSlotLoadDeps {
    return {
      getNowLocal: () => this.debug.getNowLocal(),
      locale: this.settings.locale,
      watcher: this.docWatcher,
      // assigning here, rather than from the load's return value, keeps the
      // page's cached plan refreshed at the moment the plan is read — the same
      // point the pre-move code assigned it. Assigning at the end of the load
      // instead would leave this.plan stale (or, on a first load, undefined)
      // whenever a later step of the load throws, and the workoutName /
      // workoutPlanReference getters both fall back to blank without it.
      loadPlanAsync: async watcher => {
        this.plan = await this.userDb.loadPlanAsync(watcher);
        return this.plan;
      },
      loadFitnessStateAsync: watcher => this.userDb.loadFitnessStateAsync(watcher),
      loadDeclaredStateAsync: watcher => this.userDb.loadDeclaredStateAsync(watcher),
      getLogEntriesInRangeAsync: (start, end, watcher, options) =>
        this.userDb.getLogEntriesInRangeAsync(start, end, watcher, options),
      getWorkoutModeWithContextAsync: (workout, nowLocal, watcher) =>
        this.workoutLoader.getWorkoutModeWithContextAsync(workout, this.userDb, nowLocal, watcher),
      loadWorkoutExercisesAsync: (workout, context, mode) =>
        this.workoutLoader.loadWorkoutExercisesAsync(workout, context, mode),
      reportProblem: (problemName, context) => this.logging.trackSilentProblem(problemName, context),
      getEffectivePlanReference: (workout, plan) => this.workoutLoader.getEffectivePlanReference(workout, plan),
      reportInfo: (infoName, context) => this.logging.trackSilentInfo(infoName, context),
    };
  }

  // the day copy a slot id refers to
  private slotDataFor(slot: DaySlotId): DaySlotData {
    return this.slots[slot];
  }

  // full load = light metadata (workout + mode, drives the header) then the
  // heavy exercise-list build. Used by every warm-path caller (reactive reload,
  // date change, edit-workouts return). The first page entry splits these two
  // across the slide instead — see ionViewWillEnter / ionViewDidEnter (#4242).
  private async loadWorkoutDataForSlot(slot: DaySlotId, date: DateTime) {
    await loadDaySlotAsync(this.slotDataFor(slot), date, this.slotLoadDeps());
  }

  // Loads only the LIGHT workout metadata for a slot — see loadDaySlotMetaAsync.
  // Returns the workout + mode whose exercises still need building, or null when
  // there is nothing to build.
  private async loadWorkoutMetaForSlot(load: DaySlotLoad, date: DateTime): Promise<SlotPendingBuild> {
    return loadDaySlotMetaAsync(load, date, this.slotLoadDeps());
  }

  // Starts a page entry's split load and stores it as the pending build, taking
  // the slot's claim. Called at the TOP of both ionViewWillEnter paths, before
  // the metadata await, so a day-swipe landing mid-load cannot move the slot the
  // deferred build and the loading-shell clear will use. The returned object is
  // the stored one — the caller fills in its `build` once the metadata is loaded.
  private startPendingBuild(): { slot: DaySlotId; load: DaySlotLoad; build: SlotPendingBuild } {
    const slot = this.activeDaySlot;
    const pending = { slot, load: DaySlotLoad.begin(this.slotDataFor(slot)), build: null as SlotPendingBuild };
    this.pendingBuild = pending;
    return pending;
  }

  // Builds the heavy exercise list for a slot (the ~100 ms exercise pipeline).
  private async buildSlotExercises(load: DaySlotLoad, workout: Log_Workout, mode: WorkoutLoadMode) {
    await buildDaySlotExercisesAsync(load, workout, mode, this.slotLoadDeps());
  }

  async onTitleClick() {
    const result = await this.navCtrl.callAsync(ITaskCalendarPage, {
      selectedDateISO: this.currentDay.toISO(),
      local: {
        taskStatusLoader: (startDate, endDate) => this.loadTaskStatusesForRange(startDate, endDate),
      },
    });

    if (result?.selectedDate) {
      void this.onCalendarDateSelected(result.selectedDate);
    }
  }

  async handleSettingsButtonClicked(event: Event) {
    const targetElement = event.currentTarget as HTMLElement;

    await this.popupMenu.createMenu({
      targetFromElement: targetElement,
      buttons: [
        // the Training Overview is behind the coaching dashboard switch (#5456)
        ...(this.coachingDashboardOn ? [{
          text: LL.trainingOverview.title(),
          icon: 'trending-up',
          handler: () => {
            void this.navCtrl.pushAsync(ITrainingOverviewPage, {});
          }
        }] : []),
        {
          text: LL.settings.items.trainingSettings(),
          icon: 'settings',
          handler: () => {
            void this.navCtrl.pushAsync(IProgramSettingsPage, { tab: ProgramSettingsTab.Training });
          }
        },
        {
          text: LL.settings.items.workouts(),
          icon: 'assignment',
          handler: () => {
            void this.navCtrl.pushAsync(IEditWorkoutsPage, { mode: 'edit', returnCallback: 'onEditWorkoutsResult' });
          }
        },
        {
          text: LL.settings.items.editEquipment(),
          icon: 'exercise',
          handler: () => {
            void this.navCtrl.pushAsync(IEquipmentSearchPage, { exercise: null });
          }
        }
      ]
    });
  }

  onExerciseHeaderClick(exercise: UiExercise) {
    this.handleExerciseDescription(exercise);
  }

  async onExerciseMoreOptions(event: { event: Event, exercise: UiExercise }) {
    event.event.stopPropagation();
    const targetElement = event.event.target as HTMLElement;
    const e = event.exercise;
    const isScheduled = this.isExerciseScheduled(e);
    const mode = this.activeSlotData.workoutMode;

    const buttons: PopupMenuButton[] = [];

    // Description - always shown
    buttons.push({
      text: LL.workouts.buttons.description(),
      icon: 'logo-youtube',
      handler: () => this.handleExerciseDescription(e)
    });

    // Stats - always shown
    buttons.push({
      text: LL.workouts.buttons.stats(),
      icon: 'trending-up',
      handler: () => this.handleExerciseStats(e)
    });

    // Settings - only for non-Past + Scheduled
    if (mode !== WorkoutLoadMode.Past && isScheduled) {
      buttons.push({
        text: LL.workouts.buttons.settings(),
        icon: 'settings',
        handler: () => this.handleExerciseSettings(e)
      });
    }

    // Change Exercise - only for non-Past + Scheduled
    if (mode !== WorkoutLoadMode.Past && isScheduled) {
      buttons.push({
        text: LL.workouts.buttons.changeExercise(),
        icon: 'swap-horizontal',
        handler: () => void this.handleChangeExercise(e)
      });
    }

    await this.popupMenu.createMenu({
      targetFromElement: targetElement,
      buttons,
    });
  }

  private isExerciseScheduled(e: UiExercise): boolean {
    return !e.isCompleted() && !e.isIncomplete();
  }

  private handleExerciseDescription(e: UiExercise) {
    void this.navCtrl.pushAsync(IExerciseStatsPage, { id: e.exercise._id, mode: 'instructions' });
  }

  private handleExerciseStats(e: UiExercise) {
    void this.navCtrl.pushAsync(IExerciseStatsPage, { id: e.exercise._id, mode: 'log' });
  }

  private handleExerciseSettings(e: UiExercise) {
    void this.navCtrl.pushAsync(IExerciseStatsPage, { id: e.exercise._id, mode: 'settings' });
  }

  private async handleChangeExercise(e: UiExercise) {
    // load required context on-demand
    const [plan, fitnessState, workout] = await Promise.all([
      this.userDb.loadPlanAsync(this.docWatcher),
      this.userDb.loadFitnessStateAsync(this.docWatcher),
      this.userDb.getOneLogEntryAsync<Log_Workout>(this.workoutId, this.docWatcher)
    ]);

    if (!workout) return;

    // remember which workout and which exercise the picker is for (can't pass UiExercise
    // directly through navigation). the page can reload onto another workout while the
    // picker is open, so the index alone is not enough (df-fdac90a65bdb, #4634)
    const returnContext: TrainingSwitchCapture = {
      exerciseIndex: this.activeSlotData.exercises.indexOf(e),
      logWorkoutId: workout._id,
      subSlotId: e.logExercise.subSlotId,
    };

    const effectivePlanRef = this.workoutLoader.getEffectivePlanReference(workout, plan);
    void this.navCtrl.pushAsync(IExerciseSwitchPage, {
      exerciseId: e.exercise._id,
      subSlotId: e.logExercise.subSlotId,
      workoutId: String.fromCharCode(effectivePlanRef + 'A'.charCodeAt(0)),
      returnCallback: 'onExerciseSwitched',
      warnBeforeLinkingWorkouts: true,
      local: {
        fitnessPlan: plan,
        fitnessState: fitnessState,
        returnContext,
      },
    });
  }

  async onExerciseSwitched(context: TrainingSwitchCapture, newExerciseId: string) {
    // if page was closed without selecting (e.g. header back button), ignore
    if (!newExerciseId) return;

    // if the page moved to another workout, or the exercise left this one, while the picker
    // was open, change nothing: applying the pick by position would hit the wrong exercise
    // in the wrong workout (df-fdac90a65bdb, #4634). same silent bail as Edit Workouts.
    const target = resolveTrainingSwitchTarget(context, this.workoutId, this.activeSlotData.exercises);
    if (target.kind !== TrainingSwitchTargetKind.Found) {
      this.logging.trackSilentProblem(
        target.kind === TrainingSwitchTargetKind.StaleWorkout
          ? 'training.switch-stale-workout'
          : 'training.switch-stale-exercise',
        {
          capturedLogWorkoutId: context.logWorkoutId,
          currentLogWorkoutId: this.workoutId,
          subSlotId: context.subSlotId,
          exerciseIndex: context.exerciseIndex,
          newExerciseId,
        }
      );
      return;
    }
    const uie = target.exercise;

    // build full loading context
    const [plan, fitnessState, declaredState, workout] = await Promise.all([
      this.userDb.loadPlanAsync(this.docWatcher),
      this.userDb.loadFitnessStateAsync(this.docWatcher),
      this.userDb.loadDeclaredStateAsync(this.docWatcher),
      this.userDb.getOneLogEntryAsync<Log_Workout>(this.workoutId, this.docWatcher),
    ]);

    if (!workout) return;

    const loadingContext: WorkoutLoadingContext = {
      plan,
      fitnessState,
      declaredState,
      nowLocal: this.debug.getNowLocal(),
      locale: this.settings.locale,
      watcher: this.docWatcher,
      currentWorkoutId: workout._id,
    };

    const mode = this.activeSlotData.workoutMode;

    try {
      await this.workoutLoader.performExerciseSwitchAsync(
        uie,
        newExerciseId,
        // derived now from the workout being shown, never the one captured when the picker opened
        this.workoutLoader.getEffectivePlanReference(workout, plan),
        plan,
        fitnessState,
        this.userDb,
        loadingContext,
        mode,
        // the user picked it in the exercise picker
        ExerciseChosenBy.User,
      );
    } catch (err) {
      this.logging.trackAbnormalException(err);
    }

    // new array reference so OnPush cs-exercise-list picks up the mutated UiExercise
    this.activeSlotData.exercises = [...this.activeSlotData.exercises];
    this.safeMarkForCheck(this.cd);
  }

  // whether the workout options menu has any items to show
  get hasWorkoutOptions(): boolean {
    return this.getWorkoutOptionButtons().length > 0;
  }

  // which actions the menu offers is decided by `getWorkoutOptionKinds`, a plain
  // module the unit tests can load; this method only turns each answer into a
  // button with its translated label and its handler
  private getWorkoutOptionButtons(): PopupMenuButton[] {
    const now = this.debug.getNowLocal();
    const kinds = getWorkoutOptionKinds({
      workoutState: this.activeSlotData.logWorkout?.state,
      loadMode: this.activeSlotData.workoutMode,
      isViewingToday: this.currentDay.hasSame(now, 'day'),
    });

    return kinds.map(kind => this.buildWorkoutOptionButton(kind));
  }

  // turns one decided action into the menu entry for it: the translated label
  // and the handler that runs when the user taps it
  private buildWorkoutOptionButton(kind: WorkoutOptionKind): PopupMenuButton {
    switch (kind) {
      case WorkoutOptionKind.Restore:
        return {
          text: LL.dayItems.skippedWorkout.restore(),
          handler: () => {
            void this.revertSkipWorkoutAsync();
          }
        };
      case WorkoutOptionKind.Skip:
        return {
          text: LL.training.skipWorkout(),
          handler: () => {
            void this.skipWorkoutAsync();
          }
        };
      case WorkoutOptionKind.ChangeWorkout:
        return {
          text: LL.training.changeWorkout.menuOption(),
          handler: () => {
            void this.changeWorkoutAsync();
          }
        };
      case WorkoutOptionKind.EditWorkout: {
        const workoutLetter = String.fromCharCode((this.workoutPlanReference || 0) + 'A'.charCodeAt(0));
        return {
          text: LL.training.editWorkout({ letter: workoutLetter }),
          handler: () => {
            void this.navCtrl.pushAsync(IEditWorkoutsPage, {
              workoutIndex: this.workoutPlanReference,
              returnCallback: 'onEditWorkoutsResult',
            });
          }
        };
      }
    }
  }

  /** #6268: the info card on a workout whose program day has no exercises opens Edit
   *  Workouts on that day, the same way the workout menu's "Edit Workout" does. */
  openEditWorkoutsForEmptyDay(slotData: DaySlotData) {
    if (!slotData.emptyProgramDay) return;
    void this.navCtrl.pushAsync(IEditWorkoutsPage, {
      workoutIndex: slotData.emptyProgramDay.workoutIndex,
      returnCallback: 'onEditWorkoutsResult',
    });
  }

  showWorkoutOptions(event: Event) {
    event.stopPropagation();
    const targetElement = event.target as HTMLElement;
    const buttons = this.getWorkoutOptionButtons();

    if (buttons.length === 0) return;

    void this.popupMenu.createMenu({
      targetFromElement: targetElement,
      buttons,
    });
  }

  private async skipWorkoutAsync() {
    const workoutId = this.workoutId;
    if (!workoutId) return;

    const loading = this.loadingCtrl.create({
      content: LL.common.loading.pleaseWait()
    });
    await loading.present();

    try {
      const workout = await this.userDb.getOneLogEntryAsync<Log_Workout>(workoutId, this.docWatcher);
      if (!workout) {
        await loading.dismiss();
        return;
      }

      const result = await this.workoutStateService.skipWorkoutAsync(workout, this.docWatcher);

      // if user cancelled, don't reload
      if (!result.skipped) {
        return;
      }

      // reload workout data and calendar
      await this.loadWorkoutDataForSlot(this.activeDaySlot, this.currentDay);
      await this.loadWorkoutTaskStatuses();
      this.publishStartableState();
      this.safeMarkForCheck(this.cd);
    } finally {
      await loading.dismiss();
    }
  }

  async revertSkipWorkoutAsync() {
    const workoutId = this.workoutId;
    if (!workoutId) return;

    const loading = this.loadingCtrl.create({
      content: LL.common.loading.pleaseWait()
    });
    await loading.present();

    try {
      const workout = await this.userDb.getOneLogEntryAsync<Log_Workout>(workoutId, this.docWatcher);
      if (!workout) {
        await loading.dismiss();
        return;
      }

      await this.workoutStateService.revertSkipWorkoutAsync(workout, this.docWatcher);

      // reload workout data and calendar
      await this.loadWorkoutDataForSlot(this.activeDaySlot, this.currentDay);
      await this.loadWorkoutTaskStatuses();
      this.publishStartableState();
      this.safeMarkForCheck(this.cd);
    } finally {
      await loading.dismiss();
    }
  }

  private async changeWorkoutAsync() {
    // load workout and plan
    const [workout, plan] = await Promise.all([
      this.userDb.getOneLogEntryAsync<Log_Workout>(this.workoutId, this.docWatcher),
      this.userDb.loadPlanAsync(this.docWatcher),
    ]);
    if (!workout) return;

    // get effective plan reference (computed on-demand if not set)
    const effectivePlanRef = this.workoutLoader.getEffectivePlanReference(workout, plan);
    // get original plan reference (always computed dynamically, ignoring any saved value)
    const originalPlanReference = this.workoutLoader.getEffectivePlanReference(workout, plan, true);

    const result = await this.navCtrl.callAsync(IChangeWorkoutPage, {
      currentPlanReference: effectivePlanRef,
      originalPlanReference,
    });

    if (result && result.newPlanReference !== effectivePlanRef) {
      const newPlanWorkout = plan.workouts[result.newPlanReference];

      this.workoutLoader.setWorkoutPlanReference(workout, result.newPlanReference);
      // store the canonical ENGLISH name on purpose (#4317) — see the matching
      // comment in workout-detail.ts. Display translation happens at the display
      // sites via formatWorkoutName(), never inside getEffectiveWorkoutName().
      this.workoutLoader.setWorkoutName(workout, 'Workout ' + newPlanWorkout.name);

      // rebuild exercises array from new workout's subSlotIds
      const newExercises: Log_WorkoutExercise[] = [];
      for (let i = 0; i < newPlanWorkout.subSlotIds.length; i++) {
        newExercises[i] = {
          subSlotId: newPlanWorkout.subSlotIds[i],
          sets: [],
        };
      }
      this.workoutLoader.setWorkoutExercises(workout, newExercises);

      // reload slot data to reflect changes
      await this.loadWorkoutDataForSlot(this.activeDaySlot, this.currentDay);
      if (this.viewDestroyed) return;
      this.activeSlotData.showWorkoutChangedInfo = true;
      this.safeMarkForCheck(this.cd);
    }
  }

  onWorkoutChangedInfoDismissed() {
    this.activeSlotData.showWorkoutChangedInfo = false;
    this.cd.markForCheck();
  }

  startWorkout() {
    // if hard future, go to edit-workouts page instead of workout-detail
    if (this.isHardFuture) {
      void this.navCtrl.pushAsync(IEditWorkoutsPage, {
        workoutIndex: this.workoutPlanReference,
        returnCallback: 'onEditWorkoutsResult',
      });
      return;
    }

    if (this.workoutId) {
      // scheduled workout - navigate with id
      void this.navCtrl.pushAsync(IWorkoutDetailPage, {
        id: this.workoutId,
        returnCallback: 'onWorkoutDetailResult',
      });
    } else {
      // no workout scheduled - navigate with date only
      // workout-detail will find the next workout letter
      void this.navCtrl.pushAsync(IWorkoutDetailPage, {
        date: this.currentDay.toISO(),
        returnCallback: 'onWorkoutDetailResult',
      });
    }
  }

  async onWorkoutDetailResult(_context: unknown, result: { workoutModified?: boolean }) {
    // if page was closed without explicit action (e.g. header back button), ignore
    if (!result) return;
    // if workout was modified, reload the slot data
    if (result.workoutModified) {
      await this.loadWorkoutDataForSlot(this.activeDaySlot, this.currentDay);
      await this.loadWorkoutTaskStatuses();
      this.safeMarkForCheck(this.cd);
    }
  }

  async onEditWorkoutsResult() {
    // reload workout data after returning from edit-workouts (plan may have changed)
    await this.loadWorkoutDataForSlot(this.activeDaySlot, this.currentDay);
    await this.loadWorkoutTaskStatuses();
    this.safeMarkForCheck(this.cd);
  }

  navigateToPendingWorkoutDay() {
    const pendingDateISO = this.activeSlotData.pendingWorkoutDateISO;
    if (pendingDateISO) {
      void this.onCalendarDateSelected(DateTime.fromISO(pendingDateISO));
    }
  }

  onPendingWorkoutWarningDismissed() {
    this.activeSlotData.showPendingWorkoutWarning = false;
    this.cd.markForCheck();
  }

  async ionViewWillEnter() {
    // subscribe to start workout event from bottom nav
    this.startWorkoutSubscription = this.pubSub.subscribe(startWorkoutFromNavEvent, () => {
      void this.startWorkout();
    });

    // create doc watcher for reactive sync
    if (!this.docWatcher) {
      this.docWatcher = this.userDb.database.createDocWatcher((_changes) => {
        if (this.viewDestroyed) return;
        // #4243: collapse the reload storm. During a warm-resume sync the watcher
        // is broadcast-notified for every untracked doc the catch-up touches
        // (mix/*, foods, old logs — the bulk of a large account), firing this
        // callback hundreds of times. Without a guard each firing launched a
        // fresh overlapping loadWorkoutDataForSlot (which mutates shared slotData
        // in place), piling up dozens of concurrent reloads and freezing the
        // page. The single-flight guard runs at most one reload plus one trailing
        // catch-up, so a burst collapses instead of piling up.
        void this.reactiveSlotReload();
      });
    }

    // subscribe to log entry saves for real-time updates (keep alive while page is in nav stack)
    if (!this.logEntrySavedReg) {
      this.logEntrySavedReg = this.pubSub.subscribe(logEntrySavedEvent, (entry) => {
        // only handle workouts
        if (entry.type !== LogRecordType.Workout) return;

        const workout = entry as Log_Workout;
        const workoutDate = getEntryLocalTimeInDesiredTimeZone(workout, this.debug.getNowLocal());

        // only update if workout is for current day
        if (workoutDate.toISODate() !== this.currentDay.toISODate()) return;

        // reload slot data and calendar (same as onWorkoutDetailResult). Shares
        // the single-flight guard with the doc watcher so a save landing mid-sync
        // does not add another overlapping reload (#4243).
        void this.reactiveSlotReload();
        // surface a task-status load rejection, like the other fire-and-forget
        // calls to it (the detached promise no longer reaches any lifecycle catch)
        void this.loadWorkoutTaskStatuses().catch(err => this.logging.trackAbnormalException(err));
      });
    }

    // subscribe to task-status cache updates so the calendar day markers
    // refresh when the cache recomputes — most importantly the #4219 one-shot
    // that fires when the catch-up sync completes after a display-only boot
    // load (keep alive while page is in nav stack, like logEntrySavedReg)
    if (!this.taskStatusCacheUpdatedReg) {
      this.taskStatusCacheUpdatedReg = this.pubSub.subscribe(TaskStatusCacheUpdatedEvent, (range) => {
        if (this.viewDestroyed) return;
        void this.loadWorkoutTaskStatuses(range).catch(err => this.logging.trackAbnormalException(err));
      });
    }

    // On re-entry (return from child pages like exercise-switch / edit-exercise), refresh
    // the active slot's workout data — local saves in child pages don't fire the docWatcher
    // (DatabaseWrapper's realtime-changes handler short-circuits on `doc._rev !==
    // cachedEntry.expectedRev` because the local save updates expectedRev to the saved rev
    // synchronously, before the change feed re-delivers — see design-docs/reactive-sync.md
    // 'When ionViewWillEnter is required as a resync point'). ionViewWillEnter is the only
    // way for changes like fork-on-edit / UDE virtual-delete to reflect on this page without
    // an app reload. Skip the one-time setup below (week labels, loading spinner) — only the
    // data fetch needs to repeat. `viewDestroyed` guards are stricter than the first-entry
    // path below (one guard between each await rather than a single guard after) to prevent
    // post-destroy markForCheck calls (NG0911 risk when the user navigates away mid-reload).
    if (this.initialLoadDone) {
      // #4242 (back/pop slide): defer the heavy exercise rebuild off the slide,
      // the same way first entry defers it off the enter slide. Load only the
      // LIGHT metadata now (during the slide) and build the exercise list in
      // ionViewDidEnter, after the slide. Running the full loadWorkoutDataForSlot
      // here ran the ~100 ms buildSlotExercises mid-slide, which blocked the main
      // thread before the entering page went `.active`; that inflated the slide's
      // stagger, so the pinned enter duration compressed and the arriving page
      // rushed in out of tempo with the leaving page. On a warm re-entry there is
      // no loading shell — the previous visit's exercise list stays visible during
      // the slide and is rebuilt in place after it. Resync correctness is kept:
      // loadWorkoutMetaForSlot re-reads the day's workout fresh from the DB, so a
      // child-page edit (fork-on-edit / UDE virtual-delete) is captured in
      // pendingBuild and reflected once the deferred build runs. capture the slot
      // and take the load's claim before the await so a day-swipe can't move them.
      const pending = this.startPendingBuild();
      pending.build = await this.loadWorkoutMetaForSlot(pending.load, this.currentDay);
      if (this.viewDestroyed) return;
      // surface the refreshed header now that logWorkout/mode are set
      this.safeMarkForCheck(this.cd);
      // not awaited on purpose — see ngOnInit: page entry must not block on
      // the task-status cache mutex; rejection surfaced here because the
      // detached promise no longer reaches cs-nav's lifecycle catch
      void this.loadWorkoutTaskStatuses().catch(err => this.logging.trackAbnormalException(err));
      this.publishStartableState();
      return;
    }
    this.initialLoadDone = true;

    // set loading on active slot. Stays here, in this hook's SYNCHRONOUS prefix
    // before the first await — design-docs/navigation.md (the page-transition
    // section) needs the shell to paint in the one Angular render that happens
    // before the entering page is made active.
    this.activeSlotData.loading = true;
    this.cd.markForCheck();

    let now = this.debug.getNowLocal();

    // if not faking today
    if (now.hasSame(DateTime.local(), 'day')) {
      this.fakeToday = '';
    } else {
      this.fakeToday =
        '[' +
        formatWeekdayDatePaddedWithYear(now, BOOT_LOCALE) +
        ']';
    }

    this.currentDayLabel = upperFirst(formatRelativeDay(now, this.currentDay));
    this.currentWeekday = this.currentDay.weekday.toFixed(0);

    // #4242: load only the LIGHT workout metadata here, DURING the slide, so the
    // header shows the workout name + Start button immediately. The HEAVY exercise
    // list build is held back — its ~100 ms of work (the exercise pipeline's
    // cache-served awaits resolve as microtasks that never yield to rendering, so
    // it runs as one long uninterrupted task) froze the enter animation when it
    // ran mid-slide. It is built in ionViewDidEnter, after the slide, while the
    // page is stationary. The content area shows its loading shell until then.
    // capture the entry slot for ionViewDidEnter (the slot the loading shell was
    // set on and the metadata is loaded for) before any await can let it change
    const pending = this.startPendingBuild();
    pending.build = await this.loadWorkoutMetaForSlot(pending.load, this.currentDay);
    if (this.viewDestroyed) return;
    // surface the header (name + Start button) now that logWorkout/mode are set
    this.safeMarkForCheck(this.cd);

    // task statuses: light, shared with Home (which stays smooth), read by the
    // calendar independently of the slide — leave it loading here.
    void this.loadWorkoutTaskStatuses().catch(err => this.logging.trackAbnormalException(err));
  }

  async ionViewDidEnter() {
    // #4242: the enter/back slide is complete, so it is safe to build the heavy
    // exercise list now — its ~100 ms of main-thread work happens while the page
    // is stationary (imperceptible) instead of mid-slide (dropped frames). This
    // runs on BOTH first entry (off the enter slide, content was showing a loading
    // shell) and a warm re-entry from a child page (off the back/pop slide, the
    // previous list was showing and is rebuilt in place). ionViewWillEnter set
    // pendingBuild on either path.

    // take the pending build and clear it in one step, so a survived build can
    // never re-run against the wrong slot on a later re-entry (that re-entry
    // starts its own in ionViewWillEnter) — this replaces the old unconditional
    // clear in the finally below, and happens even earlier.
    const pending = this.pendingBuild;
    this.pendingBuild = null;
    // the slot the pending metadata was loaded for (falls back to the current
    // slot if somehow unset). Used for both the build and the loading-shell clear
    // so a day-swipe landing mid-slide can't build into / clear the wrong slot.
    const slot = pending?.slot ?? this.activeDaySlot;

    try {
      // only build if no reactive reload replaced this slot's workout during the
      // slide. A docWatcher / logEntrySavedEvent firing mid-slide (the #4243
      // catch-up storm) runs a full loadWorkoutDataForSlot that already rebuilt
      // slotData.exercises from fresh DB state and set a NEW logWorkout object;
      // building from our captured (now stale) workout would overwrite that
      // fresher list. Reference equality holds only while no reload has touched
      // the slot, so it distinguishes "safe to build ours" from "a reload already
      // handled it" (its logWorkout differs, or is momentarily undefined mid-load).
      // #4410: no pending build means ionViewWillEnter did not hand this one a
      // load — the only way here is a second ionViewDidEnter with no
      // ionViewWillEnter between, where the pending build has already been
      // consumed. Starting a fresh load there would let this stale build take the
      // slot off whoever owns it now, which is the exact thing the shared load
      // object exists to prevent, so skip instead.
      const slotData = this.slotDataFor(slot);
      if (pending?.build && slotData.logWorkout === pending.build.workout) {
        await this.buildSlotExercises(pending.load, pending.build.workout, pending.build.mode);
      }
    } catch (err) {
      // surface a failed deferred build; the finally still reveals the page so a
      // build failure degrades to "exercise list absent" rather than a permanent
      // loading shell with no telemetry
      this.logging.trackAbnormalException(err);
    } finally {
      // always reveal the page and clear the loading shell — whether the build
      // succeeded, threw, or there was nothing to build (no-workout / skipped)
      if (!this.viewDestroyed) {
        this.slotDataFor(slot).loading = false;
        this.contentEntered = true;
        this.safeMarkForCheck(this.cd);
        // publish startable state for bottom nav
        this.publishStartableState();
      }
    }
  }

  async ionViewWillLeave() {
    // unsubscribe from start workout event (but keep logEntrySavedReg alive for real-time updates)
    this.startWorkoutSubscription?.unsubscribe();

    // clear startable state when leaving
    this.pubSub.publish(workoutStartableStateChangedEvent, { canStart: false });
  }

  // #4142 review fixes: reads the compact-feedback switch and follows it while the page lives, so the
  // exercise cards' badges match the workout page's (filled, and centred between the name and the line
  // under it). watchConfigValue fires on change only, so the one-shot read seeds the first value.
  // the value is stored even while the page is covered (viewDestroyed), because nothing reads it again
  // on re-entry; markForCheck needs no guard
  private watchCompactFeedbackSwitch() {
    void this.configDb.getConfigValueAsync<boolean>(ConfigKey.CompactWorkoutFeedback).then(value => {
      this.compactFeedback = value === true;
      this.cd.markForCheck();
    });
    // if not yet subscribed, subscribe so flipping the switch redraws the page live
    if (!this.unwatchCompactFeedback) {
      this.unwatchCompactFeedback = this.configDb.watchConfigValue<boolean>(ConfigKey.CompactWorkoutFeedback, value => {
        this.compactFeedback = value === true;
        this.cd.markForCheck();
      });
    }
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
    this.unwatchCompactFeedback?.();

    // clean up subscriptions that survive ionViewWillLeave
    if (this.logEntrySavedReg) {
      this.logEntrySavedReg.unsubscribe();
      this.logEntrySavedReg = undefined;
    }
    if (this.taskStatusCacheUpdatedReg) {
      this.taskStatusCacheUpdatedReg.unsubscribe();
      this.taskStatusCacheUpdatedReg = undefined;
    }
  }
}
